/**
 * Fleet HUD API Service
 * Thin typed wrappers over the admin-only /api/v1/fleet-hud/* mirror router
 * (backend/app/api/v1/fleet_hud.py). Every interface field below maps to a key
 * actually returned by that router — the run/escalation/session shapes come
 * from the Mongo docs orchestrator-v2.0/index.js writes (db.runs,
 * db.escalation_reports, db.sessions, db.orch_journal).
 *
 * FUNCTION MAPPINGS (frontend -> backend handler):
 * - fetchOverview() -> GET /api/v1/fleet-hud/overview -> get_overview()
 * - fetchEscalations() -> GET /api/v1/fleet-hud/escalations -> get_escalations()
 * - fetchRun() -> GET /api/v1/fleet-hud/runs/{id}/sessions -> get_run_sessions()
 * - fetchAgentHealth() -> GET /api/v1/fleet-hud/agent-health -> get_agent_health()
 * - fetchJournalTail() -> GET /api/v1/fleet-hud/runs/{id}/journal -> get_run_journal()
 * - approveHalt() -> POST /api/v1/fleet-hud/runs/{id}/approve-halt -> post_run_approve_halt()
 *
 * CONSUMERS:
 * - frontend/src/pages/FleetHud.tsx: Main page component
 * - frontend/src/components/fleet/*: Sub-components (RecentRuns, ActionRequired, FleetHealth)
 */

import { api } from '../utils/api';

// Latest-run projection from GET /fleet-hud/overview (fleet_hud.py projects
// exactly these keys out of the db.runs doc).
// Per-item terminal coordinates flattened server-side from
// phase_results[].results[] (fleet_hud._summarize_items).
export interface FleetItemSummary {
  item?: string;
  status?: string;
  reason?: string;
  pr_url?: string;
  branch?: string;
  commit?: string;
}

export interface FleetOverviewRun {
  run_id: string;
  project_id?: string;
  status?: string;
  total_phases?: number;
  current_phase?: number;
  created_at?: string;
  items_healed?: number;
  items_rolled_back?: number;
  items_summary?: FleetItemSummary[];
}

export interface FleetOverview {
  orchestrator_alive: boolean;
  journal_age_seconds: number | null;
  latest_run: FleetOverviewRun | null;
  open_escalations: number;
}

// Full db.runs doc returned by GET /fleet-hud/runs (no projection beyond _id).
export interface FleetRun {
  run_id: string;
  project_id?: string;
  status?: string;
  total_phases?: number;
  current_phase?: number;
  created_at?: string;
  updated_at?: string;
  completed_at?: string;
  // Last journal-write timestamp for the run. NOT part of the current
  // /fleet-hud/runs projection — RailScoreboard (bridge item 6) derives its
  // last-journal age from this when present and renders '—' when absent.
  journal_updated_at?: string;
  items_healed?: number;
  items_rolled_back?: number;
  escalation_reports?: string[];
  total_tokens?: number;
  phase_results?: unknown[];
  items_summary?: FleetItemSummary[];
  degradations?: unknown[];
  phase_configs?: Array<{
    phase_number?: number;
    name?: string;
    // Operator-authored mission statement + the raw task specs — the
    // what-was-built source of truth (build manifest, 2026-08-14).
    description?: string;
    tasks?: string[];
    items_per_phase?: number;
  }>;
  reconciled?: boolean;
  reconcile_reason?: string;
}

// db.escalation_reports doc (written by EscalationReporterAgent via
// saveEscalationReport — `id`/`run_id`/`updated_at` added by the state manager).
export interface FleetEscalation {
  id?: string;
  run_id?: string;
  phase?: number;
  item_id?: string;
  timeline?: unknown[];
  fixer_attempts?: unknown[];
  schema_violations?: unknown[];
  nexus_findings?: Record<string, unknown>;
  impact_warnings?: unknown[];
  error_messages?: unknown[];
  stack_traces?: string[];
  files_touched?: string[];
  root_cause_hypothesis?: string;
  severity?: string;
  recommended_manual_steps?: string;
  human_escalation_required?: boolean;
  generated_at?: string;
  updated_at?: string;
  status?: string;
  // Resolution marker read by RailScoreboard (bridge item 6): an escalation
  // is OPEN unless explicitly resolved (this flag, or status resolved/closed).
  resolved?: boolean;
  // pr_only records (pronly_* ids) share this collection but carry a
  // different shape: a human-readable reason + the parked PR's coordinates.
  reason?: string;
  pr_number?: number;
  pr_url?: string;
  commit?: string;
}

// db.sessions doc (Agent.toJSON() + run_id/updated_at from saveSession). The
// backend trims `results` to a 1200-char string and `logs` to the last 6
// 300-char lines (orchestrator.py orch_sessions).
export interface FleetSession {
  session_id?: string;
  run_id?: string;
  agent_type?: string;
  model_used?: string;
  tokens_consumed?: number;
  status?: string;
  started_at?: string;
  completed_at?: string | null;
  task?: string;
  results?: string;
  logs?: string[];
  updated_at?: string;
}

// GET /fleet-hud/runs/{run_id}/journal (index.js /orchestration/run/:id/journal).
export interface FleetJournalEntry {
  ts?: string;
  level?: string;
  message?: string;
}

export interface FleetJournal {
  run_id: string;
  count: number;
  entries: FleetJournalEntry[];
  note?: string;
}

// GET /fleet-hud/agent-health (lib/agent-health.js agentHealthReport shape).
export interface AgentHealthEntry {
  agent: string;
  seen: number;
  runs: number;
  passed: number;
  warned: number;
  failed: number;
  other: number;
  verdict: string;
}

export interface AgentHealth {
  runs_sampled: number;
  sessions_sampled: number;
  silent_core_agents: string[];
  attention_agents: string[];
  ok: boolean;
  agents: AgentHealthEntry[];
  note?: string;
}

// POST /fleet-hud/runs/{run_id}/approve-halt (index.js approve-halt relay).
export interface ApproveHaltResponse {
  approved?: boolean;
  halted_phases?: unknown[];
  next_action?: string;
  error?: string;
}

export async function fetchOverview(): Promise<FleetOverview> {
  return api.get('/api/v1/fleet-hud/overview');
}

export async function fetchRuns(limit: number = 15): Promise<FleetRun[]> {
  return api.get(`/api/v1/fleet-hud/runs?limit=${limit}`);
}

export async function fetchEscalations(limit: number = 5): Promise<FleetEscalation[]> {
  return api.get(`/api/v1/fleet-hud/escalations?limit=${limit}`);
}

export async function fetchRun(runId: string): Promise<FleetSession[]> {
  return api.get(`/api/v1/fleet-hud/runs/${encodeURIComponent(runId)}/sessions`);
}

export async function fetchJournalTail(runId: string, limit: number = 30): Promise<FleetJournal> {
  const raw = await api.get(
    `/api/v1/fleet-hud/runs/${encodeURIComponent(runId)}/journal?limit=${limit}`
  );
  // The deployed endpoint serves a BARE ARRAY of entries (probed live
  // 2026-08-14), while this type — and some consumers — expect the
  // { run_id, count, entries } object. Every consumer read `journal?.entries`
  // and got undefined, so the replay's journal pane and the bridge log-bar
  // tail were silently EMPTY in production. Normalize both shapes here, once,
  // so no consumer can silently lose the journal again.
  if (Array.isArray(raw)) {
    return { run_id: runId, count: raw.length, entries: raw };
  }
  const entries = Array.isArray(raw?.entries) ? raw.entries : [];
  return { run_id: raw?.run_id ?? runId, count: raw?.count ?? entries.length, entries };
}

export async function fetchAgentHealth(): Promise<AgentHealth> {
  return api.get('/api/v1/fleet-hud/agent-health');
}

/** Acknowledge an escalation report — it stays in the record (backend sets a
 * resolved flag; original status untouched) but leaves the attention
 * surfaces (ActionRequired list + scoreboard open count). */
export async function ackEscalation(escId: string): Promise<{ ok: boolean; id: string }> {
  return api.post(`/api/v1/fleet-hud/escalations/${encodeURIComponent(escId)}/ack`, undefined);
}

export async function approveHalt(runId: string, note?: string): Promise<ApproveHaltResponse> {
  return api.post(
    `/api/v1/fleet-hud/runs/${encodeURIComponent(runId)}/approve-halt`,
    note ? { note } : undefined
  );
}
